// Eventos de demostración (organizaciones y lugares ficticios) para desarrollar la página pública sin API.
// Uno por estilo, para revisar los 5 de un vistazo. Nada de esto se usa cuando VITE_API_URL está definido.
import type { EventStyleId, PublicEventDto } from '@cuorum/contracts/public-events';

const account = (slug: string, name: string, initials: string, accentColor: string, tagline: string | null = null): PublicEventDto['account'] => ({
  slug, name, initials, tagline, accentColor, font: null, privacyUrl: `/${slug}/privacidad`,
});

const event = (p: Partial<PublicEventDto['event']> & Pick<PublicEventDto['event'], 'slug' | 'title' | 'style'>): PublicEventDto['event'] => ({
  id: `demo-${p.slug}`,
  kicker: null,
  summary: null,
  startsAt: '2026-11-13T18:30:00-03:00',
  endsAt: '2026-11-13T20:30:00-03:00',
  timeZone: 'America/Santiago',
  coverKind: 'generated',
  coverUrl: null,
  backgroundMotion: true,
  venue: { name: 'Centro Cívico Los Aromos', address: 'Av. Central 1240, Santiago', note: 'Metro Los Aromos a 4 cuadras', mapUrl: 'https://www.openstreetmap.org/' },
  about: [],
  agenda: [],
  participation: ['poll', 'questions', 'wordcloud'],
  accessibility: ['Lengua de señas', 'Acceso para silla de ruedas'],
  registration: { state: 'open', capacity: { total: 80, taken: 48 }, question: null },
  ...p,
});

export const DEMO_EVENTS: PublicEventDto[] = [
  {
    account: account('ciudad-abierta', 'Corporación Ciudad Abierta', 'CA', '#2F5BEA'),
    event: event({
      slug: 'movilidad-2030', style: 'limpio',
      title: 'Encuentro ciudadano: ¿cómo queremos movernos en 2030?',
      about: [
        'Queremos escuchar a quienes caminan, pedalean, toman micro o manejan en el sector. Revisaremos el diagnóstico del barrio, votaremos prioridades y saldremos con un acta pública de acuerdos.',
        'Es gratis y abierto a todo público. Habrá café, espacio para coches de guagua e intérprete de lengua de señas.',
      ],
      agenda: [
        { time: '18:30', title: 'Bienvenida y café', detail: null },
        { time: '18:45', title: 'Diagnóstico del barrio', detail: 'Equipo de Movilidad' },
        { time: '19:15', title: 'Votación de prioridades en vivo', detail: null },
        { time: '20:00', title: 'Preguntas al panel y cierre', detail: null },
      ],
      registration: { state: 'open', capacity: { total: 80, taken: 48 }, question: { label: '¿Desde qué barrio vienes?', options: ['Los Aromos', 'Villa Central', 'Otro barrio'] } },
    }),
  },
  {
    account: account('biblioteca-del-valle', 'Red de Bibliotecas del Valle', 'BV', '#B4532A'),
    event: event({
      slug: 'ciclo-memoria-oral', style: 'editorial',
      kicker: 'Ciclo de charlas', title: 'Memoria oral del valle',
      summary: 'Tres historiadoras conversan sobre cómo registrar los relatos de quienes construyeron el barrio.',
      startsAt: '2026-11-20T19:00:00-03:00', endsAt: '2026-11-20T21:00:00-03:00',
      venue: { name: 'Biblioteca Central del Valle', address: 'Los Olmos 455, Valparaíso', note: null, mapUrl: null },
      about: ['Una conversación abierta, con lectura de fragmentos y preguntas del público al final.'],
      agenda: [
        { time: '19:00', title: 'Lectura de apertura', detail: null },
        { time: '19:20', title: 'Conversación', detail: 'Tres historiadoras invitadas' },
        { time: '20:30', title: 'Preguntas del público', detail: null },
      ],
      participation: ['questions'],
      registration: { state: 'open', capacity: { total: 60, taken: 21 }, question: null },
    }),
  },
  {
    account: account('emprende-sur', 'Colectivo Emprende Sur', 'ES', '#FF6B35'),
    event: event({
      slug: 'feria-primavera', style: 'bloque',
      kicker: 'Feria · Gratis', title: 'Feria de primavera de emprendedores',
      startsAt: '2026-11-28T11:00:00-03:00', endsAt: '2026-11-28T19:00:00-03:00',
      venue: { name: 'Parque Los Castaños', address: 'Av. Los Castaños s/n, Concepción', note: null, mapUrl: null },
      about: ['Más de 40 puestos de emprendimientos locales, música en vivo y talleres para niñas y niños.'],
      agenda: [
        { time: '11:00', title: 'Apertura de puestos', detail: null },
        { time: '15:00', title: 'Taller de reciclaje creativo', detail: 'Para niñas y niños' },
        { time: '17:30', title: 'Música en vivo', detail: null },
      ],
      participation: ['poll'],
      registration: { state: 'full', capacity: { total: 300, taken: 300 }, question: null },
    }),
  },
  {
    account: account('lab-digital', 'Laboratorio Digital Norte', 'LD', '#7CF0B4'),
    event: event({
      slug: 'lanzamiento-datos-abiertos', style: 'noche',
      title: 'Lanzamiento: portal de datos abiertos',
      startsAt: '2026-12-03T19:30:00-03:00', endsAt: '2026-12-03T22:00:00-03:00',
      venue: { name: 'Galpón 4', address: 'Calle Puerto 88, Antofagasta', note: null, mapUrl: null },
      about: ['Presentamos el portal, mostramos tres visualizaciones y abrimos la conversación sobre qué datos publicar después.'],
      agenda: [
        { time: '19:30', title: 'Recepción', detail: null },
        { time: '20:00', title: 'Demostración del portal', detail: null },
        { time: '20:45', title: 'Votación: ¿qué datos publicamos después?', detail: null },
      ],
      registration: { state: 'open', capacity: { total: 120, taken: 87 }, question: null },
    }),
  },
  {
    account: account('municipalidad-demo', 'Municipalidad de Ejemplo', 'ME', '#0B4F8A', 'Participación ciudadana'),
    event: event({
      slug: 'consulta-plan-regulador', style: 'institucional',
      title: 'Consulta ciudadana: actualización del plan regulador',
      startsAt: '2026-12-10T18:00:00-03:00', endsAt: '2026-12-10T20:00:00-03:00',
      venue: { name: 'Salón municipal', address: 'Plaza de Armas 1, Ejemplo', note: null, mapUrl: null },
      about: ['Presentación del anteproyecto y recepción de observaciones de vecinas y vecinos. Las observaciones quedan en el acta pública.'],
      agenda: [
        { time: '18:00', title: 'Presentación del anteproyecto', detail: 'Dirección de Obras' },
        { time: '18:40', title: 'Mesas por sector', detail: null },
        { time: '19:30', title: 'Plenario y votación de prioridades', detail: null },
      ],
      accessibility: ['Lengua de señas', 'Acceso universal', 'Espacio para coches de guagua'],
      registration: { state: 'open', capacity: { total: 150, taken: 64 }, question: { label: '¿En qué sector vives?', options: ['Norte', 'Centro', 'Sur', 'Rural'] } },
    }),
  },
];

export const DEMO_STYLES: EventStyleId[] = DEMO_EVENTS.map((e) => e.event.style);

// ───────────────────────── eventos publicados en modo demostración ─────────────────────────
// "Publicar" desde el borrador de IA guarda el evento en sessionStorage, así la página pública lo muestra sin API.
// Solo existe en esta pestaña del navegador; con la API real se reemplaza por POST /v1/accounts/:id/events.
const PUBLISHED_KEY = 'cuorum.demo.published';

export function publishedDemoEvents(): PublicEventDto[] {
  try { return JSON.parse(sessionStorage.getItem(PUBLISHED_KEY) ?? '[]') as PublicEventDto[]; } catch { return []; }
}

export interface PublishableDraft {
  style: EventStyleId;
  accent: string;
  motion: boolean;
  question: { label: string; options: string[] } | null;
  page: {
    title: string; kicker?: string | null; summary?: string | null; startsAt: string | null; endsAt: string | null; timeZone: string;
    venue: { name: string | null; address: string | null }; about: string[]; agenda: { time: string; title: string; detail?: string | null }[];
    participation: ('poll' | 'questions' | 'wordcloud')[]; capacity: { total: number; taken: number } | null;
  };
}

export function slugify(title: string): string {
  const full = title.normalize('NFD').replace(/[\u0300-\u036F]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  // Corta en un límite de palabra: "…-movilidad-de" se lee peor que "…-movilidad".
  const base = full.length <= 60 ? full : full.slice(0, 61).replace(/-[^-]*$/, '');
  return base || 'evento';
}

export function publishDemoEvent(d: PublishableDraft, a: { slug: string; name: string; initials: string }): string {
  if (!d.page.startsAt || !d.page.endsAt) throw new Error('sin fecha no se publica');
  const list = publishedDemoEvents();
  let slug = slugify(d.page.title);
  for (let i = 2; list.some((e) => e.event.slug === slug) || DEMO_EVENTS.some((e) => e.event.slug === slug); i++) slug = `${slugify(d.page.title)}-${i}`;
  const dto: PublicEventDto = {
    account: { slug: a.slug, name: a.name, initials: a.initials, tagline: null, accentColor: d.accent, font: null, privacyUrl: `/${a.slug}/privacidad` },
    event: {
      id: `demo-${slug}`, slug, title: d.page.title, kicker: d.page.kicker ?? null, summary: d.page.summary ?? null,
      startsAt: d.page.startsAt, endsAt: d.page.endsAt, timeZone: d.page.timeZone, style: d.style,
      coverKind: 'generated', coverUrl: null, backgroundMotion: d.motion,
      venue: { name: d.page.venue.name ?? 'Lugar por confirmar', address: d.page.venue.address ?? 'Dirección por confirmar', note: null, mapUrl: null },
      about: d.page.about, agenda: d.page.agenda.map((x) => ({ time: x.time, title: x.title, detail: x.detail ?? null })),
      participation: d.page.participation, accessibility: [],
      registration: { state: 'open', capacity: d.page.capacity, question: d.question },
    },
  };
  sessionStorage.setItem(PUBLISHED_KEY, JSON.stringify([dto, ...list]));
  return slug;
}
